
import React, { useState } from 'react';
import { Copy, Terminal, Database, ArrowRightLeft, FileText, ShieldCheck, ArrowLeft, Link, Map, Upload, BarChart, Lock, Activity, Shield, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const fullSchema = `-- Enable UUID and Vector extensions for AI features
create extension if not exists "uuid-ossp";
create extension if not exists vector;

-- 1. Users (Linked to Supabase Auth)
create table if not exists public.users (
  id uuid references auth.users not null primary key,
  role text, 
  username text,
  email text, 
  wallet_address text, 
  reputation_score int default 0,
  created_at timestamp with time zone default timezone('utc'::text, now()) not null
);

-- 2. Standardized Administrative Geography
create table if not exists public.admin_boundaries (
  id serial primary key,
  code text unique, 
  name_en text not null, 
  name_kh text not null,
  level text check (level in ('COUNTRY', 'PROVINCE', 'DISTRICT', 'COMMUNE', 'VILLAGE')),
  parent_id int references public.admin_boundaries(id)
);

-- 3. Data Categories
create table if not exists public.data_categories (
  id serial primary key,
  name text unique, 
  keywords text[]
);

-- 4. Data Sources
create table if not exists public.data_sources (
  id uuid default gen_random_uuid() primary key,
  name text not null, 
  type text, 
  website_url text, 
  logo_url text,
  credibility int default 80, 
  created_by uuid references auth.users(id),
  created_at timestamp with time zone default now()
);

-- 5. Public Data Ledger
create table if not exists public.public_data_points (
  id uuid default uuid_generate_v4() primary key,
  source_id uuid references public.data_sources(id),
  category_id int references public.data_categories(id),
  location_id int references public.admin_boundaries(id), 
  date_reported date,
  value_numeric numeric, 
  value_text text,
  unit text,
  tags text[],
  evidence_file_path text, 
  page_number int, 
  summary_text text,
  -- Generated Column for AI Context (Automatic Concatenation)
  content_for_ai text generated always as (
     summary_text || ' (' || COALESCE(value_text, value_numeric::text, 'N/A') || ' ' || COALESCE(unit, '') || ')'
  ) stored,
  embedding vector(768),
  created_at timestamp with time zone default timezone('utc'::text, now()) not null,
  verified_by_user_id uuid references public.users(id)
);
`;

const dashboardStatsScript = `-- CREATE ANALYTICS FUNCTION
-- This function runs on the database to calculate dashboard stats instantly.

-- 0. Cleanup
DROP FUNCTION IF EXISTS public.get_dashboard_stats();

CREATE OR REPLACE FUNCTION public.get_dashboard_stats()
RETURNS json
LANGUAGE plpgsql
SECURITY DEFINER
AS $$
DECLARE
    total_points INT;
    total_sources INT;
    total_categories INT;
    category_breakdown json;
    source_breakdown json;
    recent_activity json;
BEGIN
    -- 1. Total Counts
    SELECT count(*) INTO total_points FROM public_data_points;
    SELECT count(*) INTO total_sources FROM data_sources;
    SELECT count(*) INTO total_categories FROM data_categories;

    -- 2. By Category (Top 5)
    SELECT json_agg(t) INTO category_breakdown FROM (
        SELECT c.name, count(p.id) as value
        FROM data_categories c
        JOIN public_data_points p ON p.category_id = c.id
        GROUP BY c.name
        ORDER BY value DESC
        LIMIT 5
    ) t;

    -- 3. By Source Type
    SELECT json_agg(t) INTO source_breakdown FROM (
        SELECT s.type as name, count(p.id) as value
        FROM data_sources s
        JOIN public_data_points p ON p.source_id = s.id
        GROUP BY s.type
    ) t;

    -- 4. Recent Activity (Last 6 months by month)
    SELECT json_agg(t) INTO recent_activity FROM (
        SELECT to_char(date_trunc('month', created_at), 'Mon YYYY') as name, count(*) as value
        FROM public_data_points
        WHERE created_at > now() - interval '6 months'
        GROUP BY 1
        ORDER BY min(created_at)
    ) t;

    RETURN json_build_object(
        'total_points', total_points,
        'total_sources', total_sources,
        'total_categories', total_categories,
        'by_category', COALESCE(category_breakdown, '[]'::json),
        'by_source', COALESCE(source_breakdown, '[]'::json),
        'recent_trend', COALESCE(recent_activity, '[]'::json)
    );
END;
$$;

-- Grant access to public (anon) users so the homepage can load it
GRANT EXECUTE ON FUNCTION public.get_dashboard_stats() TO anon, authenticated, service_role;
`;

const integrityScript = `-- DATA INTEGRITY: PREVENT DUPLICATES
-- This script adds a UNIQUE constraint to ensure we don't insert the same data point twice.
-- A data point is defined as unique by: Source + Category + Location + Date.

-- 1. Create a Unique Index
-- If you try to insert a row that matches these 4 columns, it will be rejected (or skipped via upsert).
CREATE UNIQUE INDEX IF NOT EXISTS unique_data_point_idx 
ON public.public_data_points (source_id, category_id, location_id, date_reported);
`;

const migrationScript = `-- MIGRATION: Hybrid Reward System (Web2 + Web3)
-- [Content truncated for brevity - same as previous]
-- Please use the 'Survey & Rewards' tab for the full script.
`;

const policiesScript = `-- SECURE ROW LEVEL SECURITY (RLS) POLICIES
-- UPDATED: Includes ROBUST Storage Policies for 'datafi' bucket.

-- 1. Helper Function: Get User Role
-- This function bypasses RLS (SECURITY DEFINER) to safely check roles.
CREATE OR REPLACE FUNCTION public.get_user_role(user_id uuid)
RETURNS text
LANGUAGE sql
SECURITY DEFINER
SET search_path = public
STABLE
AS $$
  SELECT role FROM public.users WHERE id = user_id;
$$;

-- 2. STORAGE POLICIES (Bucket: datafi)
-- Create bucket if not exists (Public)
INSERT INTO storage.buckets (id, name, public)
VALUES ('datafi', 'datafi', true)
ON CONFLICT (id) DO UPDATE SET public = true;

-- Enable RLS on storage.objects
ALTER TABLE storage.objects ENABLE ROW LEVEL SECURITY;

-- Cleanup existing policies to avoid conflicts (Drop for both casing just in case)
DROP POLICY IF EXISTS "Agents upload files" ON storage.objects;
DROP POLICY IF EXISTS "Authenticated upload" ON storage.objects;
DROP POLICY IF EXISTS "Public read files" ON storage.objects;
DROP POLICY IF EXISTS "Users manage own files" ON storage.objects;
DROP POLICY IF EXISTS "Users view own files" ON storage.objects;
DROP POLICY IF EXISTS "Agents manage" ON storage.objects;

-- Policy 1: Allow ANY Authenticated User to Upload to 'datafi'
-- This simplifies the check to prevent role-lookup hangs during upload.
CREATE POLICY "Authenticated upload"
ON storage.objects FOR INSERT
TO authenticated
WITH CHECK ( bucket_id = 'datafi' );

-- Policy 2: Public Read Access (Required for Gemini to download via URL if public)
CREATE POLICY "Public read files"
ON storage.objects FOR SELECT
TO public
USING ( bucket_id = 'datafi' );

-- Policy 3: Agents & Owners can Manage (Update/Delete)
CREATE POLICY "Agents manage"
ON storage.objects FOR ALL
TO authenticated
USING (
  bucket_id = 'datafi' AND (
    owner = auth.uid() OR 
    public.get_user_role(auth.uid()) IN ('DATA_AGENT', 'RESEARCHER', 'CTO', 'AI_AGENT')
  )
);

-- 3. TABLE POLICIES (Users, Data Points, etc.)

ALTER TABLE public.users ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Users insert own" ON public.users;
DROP POLICY IF EXISTS "Users view own" ON public.users;
DROP POLICY IF EXISTS "Users edit own" ON public.users;
DROP POLICY IF EXISTS "CTO view all" ON public.users;
DROP POLICY IF EXISTS "CTO update roles" ON public.users;

CREATE POLICY "Users insert own" ON public.users FOR INSERT WITH CHECK (auth.uid() = id);
CREATE POLICY "Users view own" ON public.users FOR SELECT USING (auth.uid() = id);
CREATE POLICY "Users edit own" ON public.users FOR UPDATE USING (auth.uid() = id);
CREATE POLICY "CTO view all" ON public.users FOR SELECT USING (public.get_user_role(auth.uid()) = 'CTO');
CREATE POLICY "CTO update roles" ON public.users FOR UPDATE USING (public.get_user_role(auth.uid()) = 'CTO');

ALTER TABLE public.public_data_points ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Public read data" ON public.public_data_points;
DROP POLICY IF EXISTS "Agents manage data" ON public.public_data_points;

CREATE POLICY "Public read data" ON public.public_data_points FOR SELECT USING (true);
CREATE POLICY "Agents manage data" ON public.public_data_points FOR ALL USING (public.get_user_role(auth.uid()) IN ('DATA_AGENT', 'CTO'));

ALTER TABLE public.data_sources ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Public read sources" ON public.data_sources;
DROP POLICY IF EXISTS "CTO manage sources" ON public.data_sources;
DROP POLICY IF EXISTS "Team manage sources" ON public.data_sources;

CREATE POLICY "Public read sources" ON public.data_sources FOR SELECT USING (true);
-- ALLOW BOTH CTO AND DATA_AGENT to manage sources
CREATE POLICY "Team manage sources" ON public.data_sources FOR ALL USING (public.get_user_role(auth.uid()) IN ('CTO', 'DATA_AGENT'));

ALTER TABLE public.data_categories ENABLE ROW LEVEL SECURITY;
DROP POLICY IF EXISTS "Public read categories" ON public.data_categories;
DROP POLICY IF EXISTS "CTO manage categories" ON public.data_categories;

CREATE POLICY "Public read categories" ON public.data_categories FOR SELECT USING (true);
CREATE POLICY "CTO manage categories" ON public.data_categories FOR ALL USING (public.get_user_role(auth.uid()) = 'CTO');
`;

const vectorSetupScript = `-- ENABLE VECTOR SEARCH & WEBHOOKS
-- This script prepares the database to store embeddings and index them for semantic search.

-- 1. Enable Extension (if not already)
create extension if not exists vector;

-- 2. Add Embedding Columns (Survey Answers + Public Data)
ALTER TABLE public.survey_answers 
ADD COLUMN IF NOT EXISTS embedding vector(768);

ALTER TABLE public.public_data_points 
ADD COLUMN IF NOT EXISTS embedding vector(768);

-- 3. Add Context Columns
-- For Public Data: Generated column combining summary, value, unit.
ALTER TABLE public.public_data_points 
ADD COLUMN IF NOT EXISTS content_for_ai text 
GENERATED ALWAYS AS (
  summary_text || ' (' || COALESCE(value_text, value_numeric::text, 'N/A') || ' ' || COALESCE(unit, '') || ')'
) STORED;

-- For Survey Answers: Text column for resolved/rich context (Populated via Edge Function)
ALTER TABLE public.survey_answers
ADD COLUMN IF NOT EXISTS rich_context text;

-- 4. Create Indexes for Performance
CREATE INDEX IF NOT EXISTS survey_answers_embedding_idx 
ON public.survey_answers 
USING ivfflat (embedding vector_cosine_ops)
WITH (lists = 100);

CREATE INDEX IF NOT EXISTS public_data_points_embedding_idx 
ON public.public_data_points 
USING ivfflat (embedding vector_cosine_ops)
WITH (lists = 100);

-- 5. SEMANTIC SEARCH FUNCTIONS (RPC)
-- DROP first to allow return type changes (BigInt vs UUID)
DROP FUNCTION IF EXISTS match_survey_answers(vector, float, int, uuid[]);
DROP FUNCTION IF EXISTS match_public_data(vector, float, int, int[]);

-- Function A: Find similar survey answers (With Survey Filtering)
-- FIX: Return type 'id' is explicitly uuid to match table schema
CREATE OR REPLACE FUNCTION match_survey_answers (
  query_embedding vector(768),
  match_threshold float,
  match_count int,
  filter_survey_ids uuid[] DEFAULT '{}'::uuid[]
)
RETURNS TABLE (
  id uuid, 
  content text,
  similarity float
)
LANGUAGE plpgsql
AS $$
BEGIN
  RETURN QUERY
  SELECT
    sa.id,
    COALESCE(sa.rich_context, sa.answer_value::text) as content,
    1 - (sa.embedding <=> query_embedding) as similarity
  FROM survey_answers sa
  JOIN survey_questions sq ON sa.question_id = sq.id
  WHERE 1 - (sa.embedding <=> query_embedding) > match_threshold
  AND (cardinality(filter_survey_ids) = 0 OR sq.survey_id = ANY(filter_survey_ids))
  ORDER BY sa.embedding <=> query_embedding
  LIMIT match_count;
END;
$$;

-- Function B: Find similar public data points (With Category Filtering)
CREATE OR REPLACE FUNCTION match_public_data (
  query_embedding vector(768),
  match_threshold float,
  match_count int,
  filter_category_ids int[] DEFAULT '{}'::int[]
)
RETURNS TABLE (
  id uuid,
  content text,
  similarity float
)
LANGUAGE plpgsql
AS $$
BEGIN
  RETURN QUERY
  SELECT
    pdp.id,
    pdp.content_for_ai as content,
    1 - (pdp.embedding <=> query_embedding) as similarity
  FROM public_data_points pdp
  WHERE pdp.embedding IS NOT NULL
  AND pdp.superseded_by IS NULL                       -- never surface replaced figures
  AND 1 - (pdp.embedding <=> query_embedding) > match_threshold
  AND (cardinality(filter_category_ids) = 0 OR pdp.category_id = ANY(filter_category_ids))
  ORDER BY pdp.embedding <=> query_embedding
  LIMIT match_count;
END;
$$;

-- Function B2: KEYWORD fallback for public data (no embedding needed).
-- The oracle calls this when semantic search returns nothing — so a query still
-- finds data when embeddings lag behind inserts or the phrasing is out-of-distribution.
-- Ranks by simple full-text relevance over the AI content + summary + value text.
CREATE OR REPLACE FUNCTION keyword_public_data (
  search_query text,
  match_count int,
  filter_category_ids int[] DEFAULT '{}'::int[]
)
RETURNS TABLE (
  id uuid,
  content text,
  similarity float
)
LANGUAGE plpgsql
AS $$
BEGIN
  RETURN QUERY
  SELECT
    pdp.id,
    COALESCE(pdp.content_for_ai, pdp.summary_text, pdp.value_text) as content,
    ts_rank(
      to_tsvector('simple', COALESCE(pdp.content_for_ai,'') || ' ' || COALESCE(pdp.summary_text,'') || ' ' || COALESCE(pdp.value_text,'')),
      websearch_to_tsquery('simple', search_query)
    )::float as similarity
  FROM public_data_points pdp
  WHERE pdp.superseded_by IS NULL
  AND (cardinality(filter_category_ids) = 0 OR pdp.category_id = ANY(filter_category_ids))
  AND to_tsvector('simple', COALESCE(pdp.content_for_ai,'') || ' ' || COALESCE(pdp.summary_text,'') || ' ' || COALESCE(pdp.value_text,''))
      @@ websearch_to_tsquery('simple', search_query)
  ORDER BY similarity DESC
  LIMIT match_count;
END;
$$;

-- 6. INSTRUCTIONS FOR WEBHOOKS (Manual Step)
-- Run this SQL in step 1. Step 2 is configuring the triggers in the Supabase UI.

/*
  WEBHOOK 1: Embed Survey Answers
  1. Go to Database -> Webhooks -> Create Webhook.
  2. Name: "Embed Answer".
  3. Table: "survey_answers". Events: "Insert".
  4. Type: "HTTP Request" (POST).
  5. URL: https://[YOUR_REF].functions.supabase.co/embed-survey-answer
  6. Add Header: "Authorization" = "Bearer [YOUR_ANON_KEY]"

  WEBHOOK 2: Embed Public Data
  1. Go to Database -> Webhooks -> Create Webhook.
  2. Name: "Embed Public Data".
  3. Table: "public_data_points". 
  4. Events: "Insert", "Update". (CHECK BOTH!)
  5. Type: "HTTP Request" (POST).
  6. URL: https://[YOUR_REF].functions.supabase.co/embed-public-data
  7. Add Header: "Authorization" = "Bearer [YOUR_ANON_KEY]"
*/
`;

const SchemaView: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'FULL' | 'DASHBOARD' | 'INTEGRITY' | 'MIGRATION' | 'POLICIES' | 'VECTORS'>('FULL');
  const navigate = useNavigate();

  const copyToClipboard = () => {
    let text = fullSchema;
    if (activeTab === 'DASHBOARD') text = dashboardStatsScript;
    if (activeTab === 'INTEGRITY') text = integrityScript;
    if (activeTab === 'MIGRATION') text = migrationScript;
    if (activeTab === 'POLICIES') text = policiesScript;
    if (activeTab === 'VECTORS') text = vectorSetupScript;
    
    navigator.clipboard.writeText(text);
    alert('SQL Script copied to clipboard');
  };

  const getScriptName = () => {
    switch (activeTab) {
        case 'FULL': return 'schema_complete.sql';
        case 'DASHBOARD': return 'create_rpc_stats.sql';
        case 'INTEGRITY': return 'integrity_constraint.sql';
        case 'MIGRATION': return 'migration_survey.sql';
        case 'POLICIES': return 'rls_policies_v2.sql';
        case 'VECTORS': return 'vector_setup.sql';
        default: return 'script.sql';
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 p-4 md:p-8">
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate('/')} className="mb-4 text-slate-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft size={16} /> Back to App
        </button>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
            <h1 className="text-xl md:text-2xl font-bold text-white mb-2">Database Schema & Repair</h1>
            <p className="text-slate-400 text-sm md:text-base">
                Manage your Supabase database structure and RLS policies.
            </p>
            </div>
            
            <div className="flex flex-wrap bg-slate-800 p-1 rounded-lg gap-1">
                <button 
                    onClick={() => setActiveTab('FULL')}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'FULL' ? 'bg-slate-700 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    <div className="flex items-center gap-2"><Database size={14}/> Full Setup</div>
                </button>
                <button 
                    onClick={() => setActiveTab('VECTORS')}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'VECTORS' ? 'bg-purple-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    <div className="flex items-center gap-2"><Sparkles size={14}/> Vector AI</div>
                </button>
                <button 
                    onClick={() => setActiveTab('POLICIES')}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'POLICIES' ? 'bg-emerald-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    <div className="flex items-center gap-2"><Shield size={14}/> RLS Policies (Fix)</div>
                </button>
                <button 
                    onClick={() => setActiveTab('DASHBOARD')}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'DASHBOARD' ? 'bg-blue-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    <div className="flex items-center gap-2"><BarChart size={14}/> Dashboard RPC</div>
                </button>
                <button 
                    onClick={() => setActiveTab('INTEGRITY')}
                    className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${activeTab === 'INTEGRITY' ? 'bg-red-600 text-white shadow' : 'text-slate-400 hover:text-slate-200'}`}
                >
                    <div className="flex items-center gap-2"><Lock size={14}/> Prevent Dupes</div>
                </button>
            </div>
        </div>
      </div>

      <div className="bg-slate-950 rounded-xl overflow-hidden shadow-2xl border border-slate-800">
        <div className="bg-slate-900 px-4 py-3 flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-2">
            <Terminal size={14} className="text-slate-500" />
            <span className="text-xs text-slate-500 font-mono">
                {getScriptName()}
            </span>
          </div>
          <button 
             onClick={copyToClipboard}
             className="text-xs flex items-center gap-1 text-indigo-400 hover:text-indigo-300"
           >
             <Copy size={12} /> Copy Script
           </button>
        </div>
        <div className="p-4 md:p-6 overflow-x-auto">
          <pre className="font-mono text-xs md:text-sm leading-relaxed">
            <code className="text-blue-300">
                {(
                  activeTab === 'FULL' ? fullSchema : 
                  activeTab === 'POLICIES' ? policiesScript :
                  activeTab === 'DASHBOARD' ? dashboardStatsScript :
                  activeTab === 'INTEGRITY' ? integrityScript :
                  activeTab === 'VECTORS' ? vectorSetupScript :
                  migrationScript
                ).split('\n').map((line, i) => (
                    <div key={i} className="table-row">
                        <span className="table-cell select-none text-slate-700 text-right pr-4 w-6 md:w-8">{i + 1}</span>
                        <span className={`table-cell ${line.trim().startsWith('--') || line.trim().startsWith('/*') ? 'text-slate-500 italic' : ''}`}>
                            {line}
                        </span>
                    </div>
                ))}
            </code>
          </pre>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SchemaView;
